import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { fetchNewsById, NewsArticle } from '../../services/api/newsService';
import { TaskyErrorState, TaskySpinner } from '../../components/tasky';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

/**
 * ヒーロー（サムネイル画像／トップ動画）の寸法。画像は object-cover で枠を埋め、
 * 動画は object-contain で切れないようにするため、サイズ指定だけを共有する。
 */
const HERO_MEDIA_SIZE = 'w-full h-full max-h-[400px]';

const NewsDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<NewsArticle | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadArticle = async () => {
      if (!id) return;
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchNewsById(Number(id));
        setArticle(data);
      } catch (err) {
        console.error('Failed to fetch news article:', err);
        setError('記事の読み込みに失敗しました。');
      } finally {
        setIsLoading(false);
      }
    };

    loadArticle();
  }, [id]);

  // 本文は管理画面で入力された HTML。描画前にサニタイズする（毎レンダーの
  // 再計算を避けるため元 HTML をキーにメモ化）。
  const rawContent = article?.content ?? '';
  const sanitizedContent = useMemo(() => sanitizeHtml(rawContent), [rawContent]);

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="h-[100dvh] flex flex-col overflow-x-hidden bg-white font-sans text-dark">
      {/* Breadcrumb header */}
      <header className="min-h-14 bg-white px-3 py-2 flex items-center gap-2 flex-shrink-0 border-b border-warm-border sm:px-6 sm:py-0">
        <button
          type="button"
          data-tutorial="news-detail-back"
          onClick={() => navigate('/news')}
          className="flex items-center justify-center w-8 h-8 rounded-tasky-sm text-gray-400 hover:bg-warm-hover hover:text-gray-600 transition-colors mr-1"
          aria-label="ニュース一覧に戻る"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <ChevronRight className="w-[18px] h-[18px] text-gray-300 flex-shrink-0" />
        <button
          type="button"
          onClick={() => navigate('/news')}
          className="text-sm text-gray-400 hover:text-primary transition-colors flex-shrink-0"
        >
          ニュース
        </button>
        {article && (
          <>
            <ChevronRight className="w-[18px] h-[18px] text-gray-300 flex-shrink-0" />
            <span className="text-sm text-dark font-medium truncate max-w-[160px] sm:max-w-[360px]">
              {article.title}
            </span>
          </>
        )}
      </header>

      {/* Scrollable article area */}
      <main className="flex-1 overflow-y-auto bg-white">
        <div className="max-w-[760px] mx-auto px-4 py-6 sm:px-6 sm:py-8">
          {isLoading && (
            <div className="flex flex-col items-center gap-2 py-12">
              <TaskySpinner size="md" />
              <span className="text-sm text-gray-500">読み込み中...</span>
            </div>
          )}

          {error && (
            <TaskyErrorState title="記事の読み込みに失敗しました" description={error} />
          )}

          {!isLoading && !error && article && (
            <article>
              {/* Hero: トップ動画があれば自動再生（ミュート必須）、無ければ従来のサムネイル画像 */}
              {(article.hero_video_url || article.thumbnail_url) && (
                  <div className="w-full max-h-[400px] rounded-tasky-md overflow-hidden mb-6 bg-warm-surface sm:mb-8">
                  {article.hero_video_url ? (
                    <video
                      data-testid="news-hero-video"
                      src={article.hero_video_url}
                      poster={article.thumbnail_url ?? undefined}
                      autoPlay
                      muted
                      loop
                      playsInline
                      controls
                      preload="metadata"
                      className={`${HERO_MEDIA_SIZE} object-contain bg-black`}
                    />
                  ) : (
                    <img
                      data-testid="news-hero-image"
                      src={article.thumbnail_url ?? undefined}
                      alt={article.title}
                      className={`${HERO_MEDIA_SIZE} object-cover`}
                    />
                  )}
                </div>
              )}

              {/* Published date */}
              <time className="block text-xs text-gray-400 mb-4">
                {formatDate(article.published_at || article.created_at)}
              </time>

              {/* Title */}
              <h1 className="text-xl font-bold text-dark leading-snug mb-5 sm:text-2xl sm:mb-6">
                {article.title}
              </h1>

              {/* Divider */}
              <div className="border-t border-warm-border mb-8" />

              {/* Content rendered as HTML */}
              <div
                className={[
                  'text-[0.9375rem] text-dark leading-[1.85]',
                  '[&_h1]:text-xl [&_h1]:font-bold [&_h1]:text-dark [&_h1]:mt-8 [&_h1]:mb-3 [&_h1]:leading-normal',
                  '[&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-dark [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:leading-normal',
                  '[&_h3]:text-base [&_h3]:font-bold [&_h3]:text-dark [&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:leading-normal',
                  '[&_p]:mb-4',
                  '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4',
                  '[&_ol]:list-decimal [&_ol]:pl-7 [&_ol]:mb-4',
                  '[&_li]:mb-1.5 [&_li]:leading-relaxed',
                  '[&_strong]:font-bold [&_strong]:text-dark',
                  '[&_a]:text-primary [&_a]:underline',
                  '[&_img]:max-w-full [&_img]:rounded-tasky-md [&_img]:my-4',
                  '[&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto',
                  '[&_pre]:max-w-full [&_pre]:overflow-x-auto',
                  '[&_blockquote]:bg-warm-surface [&_blockquote]:rounded-tasky-md [&_blockquote]:px-6 [&_blockquote]:py-5 [&_blockquote]:mb-5',
                ].join(' ')}
                dangerouslySetInnerHTML={{ __html: sanitizedContent }}
              />

              {/* Back to list */}
              <div className="mt-10 pt-6">
                <button
                  type="button"
                  onClick={() => navigate('/news')}
                  className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  ニュース一覧に戻る
                </button>
              </div>
            </article>
          )}
        </div>
      </main>
    </div>
  );
};

export default NewsDetailPage;
